import React from 'react';

import Alert from '@/assets/icons/alert';
import Checkmark from '@/assets/icons/checkmark';
import { Button } from '@/components/ButtonComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Banner } from '@/feature/betslip-sbk/components/Banner';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';

import { useCancelWithdrawalAction } from '../hooks/use-cancel-withdrawal-action';
import { WithdrawalCancellationState } from '../utils/withdrawal-cancellation';

const BANNER_ICON_SIZE = 24;

type CancelWithdrawalBannerProps = {
    variant: 'success' | 'error';
    message: string;
    testID: string;
};

const CancelWithdrawalBanner = ({ variant, message, testID }: CancelWithdrawalBannerProps) => {
    const isSuccess = variant === 'success';
    return (
        <Box mb="s16">
            <Banner
                message={message}
                textVariant="bodyMedium"
                backgroundColor={
                    isSuccess ? ds.colorToken.background.success.subtle : ds.colorToken.background.danger.subtle
                }
                borderColor={
                    isSuccess ? ds.colorToken.background.success.strong : ds.colorToken.background.danger.strong
                }
                borderWidth={1}
                leftIcon={
                    isSuccess ? (
                        <Checkmark
                            width={BANNER_ICON_SIZE}
                            height={BANNER_ICON_SIZE}
                            color={ds.colorValue.icon.success}
                        />
                    ) : (
                        <Alert width={BANNER_ICON_SIZE} height={BANNER_ICON_SIZE} color={ds.colorValue.icon.danger} />
                    )
                }
                testID={testID}
            />
        </Box>
    );
};

type CancelWithdrawalActionProps = {
    userWalletId: string;
    state: WithdrawalCancellationState | null;
};

export const CancelWithdrawalAction = ({ userWalletId, state }: CancelWithdrawalActionProps) => {
    const t = useSocialWalletTranslation('transaction_details');
    const { handlePress, isPending, blockedMessage } = useCancelWithdrawalAction(userWalletId);

    if (!state) {
        return blockedMessage ? (
            <CancelWithdrawalBanner variant="error" message={blockedMessage} testID="cancelWithdrawalBlocked" />
        ) : null;
    }

    if (state === 'cancelled') {
        return (
            <CancelWithdrawalBanner
                variant="success"
                message={t('cancel_withdrawal_cancelled')}
                testID="cancelWithdrawalSuccess"
            />
        );
    }

    if (state === 'approved') {
        return (
            <CancelWithdrawalBanner
                variant="error"
                message={t('cancel_withdrawal_processed')}
                testID="cancelWithdrawalApproved"
            />
        );
    }

    if (blockedMessage) {
        return <CancelWithdrawalBanner variant="error" message={blockedMessage} testID="cancelWithdrawalBlocked" />;
    }

    return (
        <Box mb="s16">
            <Button
                label={t('common:cancel')}
                hierarchy="secondary"
                loading={isPending}
                disabled={isPending}
                onPress={handlePress}
                testID="txnDetails-cancelWithdrawal"
            />
        </Box>
    );
};
